import React, { useEffect, useState, useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { getBackendUrl } from '../utils/apiUrl';
import { hasIncompleteSections } from './Provider/OnboardingTutorial';

const BACKEND_URL = getBackendUrl();

/**
 * ProfileCompletionGuard — wraps provider routes that depend on a completed
 * profile (e.g. /provider/discover, where matches are computed from the
 * provider's preferences). If any step of the profile walk is unanswered,
 * redirect to /provider/profile?setup=required (with state.from) so the
 * provider finishes setup before they hit a feature that won't work without it.
 * The walk reads state.from and says which place it will open.
 *
 * The check is the journey's isProfileSetupComplete (via hasIncompleteSections),
 * the same rule the walk itself finishes on, so the walk can never hand a
 * provider to a route that bounces them back.
 *
 * The check result is cached in sessionStorage, per user, to avoid repeated
 * API calls; it is busted via invalidateProfileSetupCache() after every save
 * and on logout, so a second account in the same tab never inherits it.
 *
 * The redirect carries the reason in the URL (?for=vault), not only in router
 * state, so the walk can still say which place it opens after a reload.
 */
const CACHE_PREFIX = 'pv_profile_setup';
const cacheKey = (user) => {
    const id = user?._id || user?.id || user?.sub;
    return id ? `${CACHE_PREFIX}:v2:${id}` : null;
};
const ProfileCompletionGuard = ({ children, allowReferralReward = false }) => {
    const { auth } = useContext(AuthContext);
    const location = useLocation();
    const key = cacheKey(auth?.user);
    const referralRewardRequested = allowReferralReward &&
        new URLSearchParams(location.search).get('referralReward') === '1';
    const [status, setStatus] = useState('loading'); // 'loading' | 'complete' | 'incomplete'

    useEffect(() => {
        // Referral milestone emails must remain usable for providers who have
        // not completed every matching-preference section yet. Discover owns
        // the reward UI as well as the swipe deck, so permit only its explicit
        // reward deep link; ordinary Discover visits still require setup.
        if (referralRewardRequested) {
            setStatus('complete');
            return;
        }

        if (!auth?.token) {
            setStatus('complete'); // let RoleBasedRoute handle auth
            return;
        }

        // Check sessionStorage cache first
        const cached = key ? sessionStorage.getItem(key) : null;
        if (cached === 'complete') {
            setStatus('complete');
            return;
        }

        // Fetch profile and check required fields
        const checkProfile = async () => {
            try {
                const res = await fetch(`${BACKEND_URL}/provider/me/profile`, {
                    headers: { Authorization: `Bearer ${auth.token}` },
                });
                if (!res.ok) {
                    setStatus('complete'); // if API fails, don't block
                    return;
                }
                const raw = await res.json();
                // Handle both { ...profile } and { data: { ...profile } }
                const profile = raw?.data || raw;

                // Same predicate the completion banner uses — a profile is "complete"
                // only when every one of the five preference sections has its
                // required fields filled. Matching needs all of them.
                if (hasIncompleteSections(profile)) {
                    setStatus('incomplete');
                } else {
                    if (key) sessionStorage.setItem(key, 'complete');
                    setStatus('complete');
                }
            } catch (err) {
                console.error('[ProfileCompletionGuard] Error checking profile:', err);
                setStatus('complete'); // don't block on errors
            }
        };

        checkProfile();
    }, [auth?.token, key, referralRewardRequested]);

    if (status === 'loading') {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                color: 'var(--provider-slate-400, #94a3b8)',
                fontSize: '0.9rem',
            }}>
                Loading…
            </div>
        );
    }

    if (status === 'incomplete') {
        const place = String(location.pathname || '').split('/')[2] || '';
        const query = place ? `?setup=required&for=${encodeURIComponent(place)}` : '?setup=required';
        return (
            <Navigate
                to={`/provider/profile${query}`}
                replace
                state={{ from: location.pathname }}
            />
        );
    }

    return children;
};

/**
 * Call this when the user updates their profile preferences
 * to invalidate the cached profile completion status.
 */
export const invalidateProfileSetupCache = () => {
    try {
        Object.keys(sessionStorage)
            .filter((k) => k === CACHE_PREFIX || k.startsWith(`${CACHE_PREFIX}:`))
            .forEach((k) => sessionStorage.removeItem(k));
    } catch {
        // Storage blocked: nothing cached to clear.
    }
};

export default ProfileCompletionGuard;
